import type { Tour } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Tours guiados. Os alvos (`target`) usam "resolver strings" entendidas por
// resolveTarget() — não precisam de marcação nas páginas:
//   nav=/rota         → item da sidebar
//   text=Rótulo       → botão/link cujo texto contém "Rótulo"
//   css=seletor       → seletor CSS cru
//   (sem target)      → passo centralizado, tipo intro
// ─────────────────────────────────────────────────────────────────────────────

export const TOURS: Tour[] = [
  {
    id: 'overview',
    route: '/dashboard',
    autoRun: true,
    steps: [
      {
        title: 'Bem-vindo ao MyShow! 👋',
        body: 'Em 1 minuto te mostro como o painel funciona. Você pode pular quando quiser e refazer este tour pelo botão de ajuda (?) no canto da tela.',
      },
      { target: 'nav=/dashboard', title: 'Dashboard', body: 'Sua visão geral: status das TVs, horas de conteúdo exibido e alertas. É a tela inicial.', placement: 'right' },
      { target: 'nav=/tvs', title: 'TVs', body: 'Onde você conecta e gerencia suas telas. Cada TV mostra se está online e o que está exibindo.', placement: 'right' },
      { target: 'nav=/playlists', title: 'Playlists', body: 'Aqui você monta o conteúdo que toca nas telas — a partir de modelos prontos ou do zero.', placement: 'right' },
      { target: 'nav=/products', title: 'Produtos', body: 'Seu catálogo de preços e promoções, que alimenta os slides de preço e os carrosséis.', placement: 'right' },
      { target: 'nav=/billing', title: 'Cobrança', body: 'Seu plano, faturas e quantidade de TVs contratadas.', placement: 'right' },
      {
        title: 'Pronto para começar! 🚀',
        body: 'Sugestão: comece conectando uma TV e criando uma playlist a partir de um modelo. Bom trabalho!',
      },
    ],
  },
  {
    id: 'stores',
    route: '/stores',
    autoRun: true,
    steps: [
      { title: 'Suas lojas', body: 'Cada loja é uma unidade do seu negócio. TVs e produtos podem ser vinculados a uma loja específica.' },
      { target: 'text=Nova loja', title: 'Cadastrar uma loja', body: 'Comece por aqui. Use o mesmo código da loja no seu ERP para a sincronização funcionar.', placement: 'left' },
    ],
  },
  {
    id: 'tvs',
    route: '/tvs',
    autoRun: true,
    steps: [
      { title: 'Central de TVs', body: 'Todas as suas telas em um só lugar. O ponto verde pulsando indica que a TV está online agora.' },
      { target: 'text=Adicionar TV', title: 'Conectar uma TV', body: 'Clique aqui para parear uma nova tela usando o código que aparece nela.', placement: 'left' },
      { target: 'css=input[placeholder*="Buscar"]', title: 'Busca e filtros', body: 'Encontre rápido pelo nome, ou filtre por status e por loja.', placement: 'bottom' },
    ],
  },
  {
    id: 'tvs-add',
    route: '/tvs/add',
    autoRun: true,
    steps: [
      { title: 'Pareamento plug-and-play', body: 'Conectar uma TV tem só 4 passos. O quadro à esquerda mostra o passo a passo.' },
      { target: 'css=input[maxlength="8"], input[placeholder*="ABC"]', title: 'O código da TV', body: 'Digite aqui os 8 caracteres que aparecem na tela. Ele expira em poucos minutos — se expirar, pegue o novo na TV.', placement: 'top' },
      { target: 'text=Parear TV', title: 'Finalizar', body: 'Com o código preenchido, clique em Parear TV. Pronto, a tela vira sua!', placement: 'top' },
    ],
  },
  {
    id: 'playlists',
    route: '/playlists',
    autoRun: true,
    steps: [
      { title: 'Suas playlists', body: 'Playlist é a sequência de slides que toca em loop nas TVs.' },
      { target: 'text=Usar modelo', title: 'Caminho mais rápido', body: 'Comece de um modelo pronto do seu segmento e só personalize. Bem mais rápido que montar do zero.', placement: 'bottom' },
      { target: 'text=Em branco', title: 'Total liberdade', body: 'Ou crie uma playlist em branco e monte cada slide do seu jeito.', placement: 'bottom' },
    ],
  },
  {
    id: 'playlist-editor',
    route: '/playlists/new',
    routeMatch: /^\/playlists\/(?!templates$)[^/]+$/,
    autoRun: true,
    steps: [
      { title: 'Editor de playlist', body: 'À esquerda ficam os slides; no centro, o editor do slide selecionado.' },
      { title: 'Escolha a orientação primeiro', body: 'Deitada (16:9) para TVs na horizontal, ou Em pé (9:16) para totens verticais. Defina isso antes de montar os slides — mudar depois pode quebrar o layout.' },
    ],
  },
  {
    id: 'products',
    route: '/products',
    autoRun: true,
    steps: [
      { title: 'Catálogo de produtos', body: 'Estes produtos alimentam os slides de preço, tabelas e carrosséis nas suas telas.' },
      { target: 'text=Novo produto', title: 'Cadastrar manualmente', body: 'Adicione produtos um a um, com preço, promoção e foto.', placement: 'left' },
      { target: 'text=Importar', title: 'Importar em massa', body: 'Suba vários produtos de uma vez por arquivo CSV/Toledo — ou use o Agente para sincronizar com seu ERP.', placement: 'left' },
    ],
  },
  {
    id: 'tvs-detail',
    route: '/tvs/_detail',
    routeMatch: /^\/tvs\/(?!add$|sessions$)[^/]+$/,
    autoRun: true,
    steps: [
      { title: 'Painel da TV', body: 'Aqui você controla uma TV específica: dados, playlist no ar e informações do aparelho.' },
      { title: 'Atribua uma playlist', body: 'No card "Playlist atual", escolha o conteúdo e clique em "Salvar playlist" — a TV passa a exibir na hora.' },
      { title: 'Informações do aparelho', body: 'O card "Dispositivo" mostra marca, modelo, memória e versão do app, reportados pela própria TV. Útil para suporte.' },
    ],
  },
  {
    id: 'tvs-sessions',
    route: '/tvs/sessions',
    autoRun: true,
    steps: [
      { title: 'Calendário de uso', body: 'Cada linha é um dia e as barras coloridas mostram quando cada TV ficou online. Ótimo para diagnosticar quedas.' },
      { target: 'text=30d', title: 'Período', body: 'Alterne entre 7, 30 e 90 dias. Você também pode filtrar por loja e por TV.', placement: 'bottom' },
    ],
  },
  {
    id: 'templates',
    route: '/playlists/templates',
    autoRun: true,
    steps: [
      { title: 'Modelos prontos', body: 'Playlists já montadas por segmento (Açougue, Padaria, Mercado, Farmácia…). O jeito mais rápido de começar.' },
      { target: 'text=Usar', title: 'Use em 1 clique', body: 'O modelo vira uma playlist sua e abre direto no editor para personalizar.', placement: 'top' },
    ],
  },
  {
    id: 'themes',
    route: '/themes',
    autoRun: true,
    steps: [
      { title: 'Temas visuais', body: 'Combinações prontas de cores e fontes para deixar seu conteúdo profissional.' },
      { title: 'Como aplicar', body: 'Os temas são escolhidos ao criar ou editar uma playlist, no seletor de tema. Esta tela serve para você conhecer as opções.' },
    ],
  },
  {
    id: 'billing',
    route: '/billing',
    autoRun: true,
    steps: [
      { title: 'Cobrança', body: 'Seu plano, faturas, quantidade de TVs contratadas e forma de pagamento.' },
      { target: 'text=Mudar plano', title: 'Plano e TVs', body: 'Troque de plano ou ajuste quantas TVs você contrata. Faturas pendentes têm o botão "Pagar".', placement: 'bottom' },
    ],
  },
  {
    id: 'settings',
    route: '/settings',
    autoRun: true,
    steps: [
      { title: 'Configurações', body: 'Dados da empresa, equipe, categorias, integração com o ERP, notificações e API — organizados em abas.' },
      { target: 'text=Usuários', title: 'Convide sua equipe', body: 'Na aba Usuários você adiciona pessoas. O papel "editor" monta conteúdo sem acessar o financeiro.', placement: 'bottom' },
    ],
  },
  {
    id: 'agent',
    route: '/settings/agent',
    autoRun: true,
    steps: [
      { title: 'Agentes (ERP)', body: 'O Agente é um programa instalado na sua empresa que lê o ERP e sincroniza produtos e preços com o MyShow.' },
      { target: 'text=Instalar novo agente', title: 'Instalar', body: 'Gere um token, baixe o instalador e rode no computador com acesso ao banco do ERP.', placement: 'left' },
    ],
  },
  {
    id: 'agent-routines',
    route: '/settings/agent/routines',
    autoRun: true,
    steps: [
      { title: 'Rotinas de sincronização', body: 'Cada rotina busca um tipo de dado no ERP (produtos, promoções, estoque, categorias) no intervalo definido.' },
      { target: 'text=Nova rotina', title: 'Criar rotina', body: 'Defina a consulta, o destino e a frequência. Você também pode executar uma rotina na hora.', placement: 'left' },
    ],
  },
  {
    id: 'agent-catalog',
    route: '/settings/agent/catalog',
    autoRun: true,
    steps: [
      { title: 'Sincronização de catálogo', body: 'Assistente em 3 passos: descobrir categorias do ERP, escolher quais exibir e gerar as rotinas automaticamente.' },
      { target: 'text=Descobrir', title: 'Comece descobrindo', body: 'O sistema lê a estrutura de departamentos e categorias do seu ERP.', placement: 'bottom' },
    ],
  },
  {
    id: 'agent-logs',
    route: '/settings/agent/logs',
    autoRun: true,
    steps: [
      { title: 'Histórico de execuções', body: 'Auditoria de todas as sincronizações: quando rodaram, se deram certo e quantas linhas processaram.' },
      { title: 'Investigando problemas', body: 'Se um preço não atualizou nas telas, comece por aqui: veja se a rotina correspondente rodou com sucesso.' },
    ],
  },
];

export function findTourById(id?: string): Tour | undefined {
  if (!id) return undefined;
  return TOURS.find((t) => t.id === id);
}

export function findTourByRoute(path: string): Tour | undefined {
  return (
    TOURS.find((t) => t.route === path) ||
    TOURS.find((t) => t.routeMatch?.test(path))
  );
}
